import { styled } from '@linaria/react';
import { isNonEmptyString } from '@sniptt/guards';
import { isDefined } from 'app-shared/utils';
import { type IconComponent } from 'app-ui/icon';
import { themeCssVariables, useTheme } from 'app-ui/theme';
import { type ReactNode } from 'react';

import { EMPTY_STATE_TEXT_MAX_WIDTH } from '@/ui/feedback/empty-state/constants/EmptyStateTextMaxWidth';

// Fills whatever holds it: the whole window, the page body or the side panel.
const StyledContainer = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${themeCssVariables.spacing[1]};
  justify-content: center;
  padding: ${themeCssVariables.spacing[8]} ${themeCssVariables.spacing[4]};
  text-align: center;
`;

const StyledIcon = styled.div`
  align-items: center;
  background: ${themeCssVariables.background.transparent.lighter};
  border: 1px solid ${themeCssVariables.border.color.medium};
  border-radius: ${themeCssVariables.border.radius.md};
  box-sizing: border-box;
  color: ${themeCssVariables.font.color.tertiary};
  display: flex;
  height: ${themeCssVariables.spacing[10]};
  justify-content: center;
  margin-bottom: ${themeCssVariables.spacing[3]};
  width: ${themeCssVariables.spacing[10]};
`;

const StyledTitle = styled.p`
  color: ${themeCssVariables.font.color.primary};
  font-size: ${themeCssVariables.font.size.md};
  font-weight: ${themeCssVariables.font.weight.semiBold};
  margin: 0;
`;

const StyledDescription = styled.p`
  color: ${themeCssVariables.font.color.tertiary};
  font-size: ${themeCssVariables.font.size.md};
  line-height: ${themeCssVariables.text.lineHeight.lg};
  margin: 0;
  max-width: ${EMPTY_STATE_TEXT_MAX_WIDTH}px;
`;

const StyledAction = styled.div`
  margin-top: ${themeCssVariables.spacing[3]};
`;

type EmptyStateProps = {
  title: string;
  description?: string;
  Icon?: IconComponent;
  action?: ReactNode;
  // 'alert' for errors, so screen readers announce them.
  role?: 'alert' | 'status';
};

// A short title, one sentence and at most one action, for "nothing here yet"
// and "this couldn't load" alike.
export const EmptyState = ({
  title,
  description,
  Icon,
  action,
  role,
}: EmptyStateProps) => {
  const theme = useTheme();

  return (
    <StyledContainer role={role}>
      {isDefined(Icon) && (
        <StyledIcon aria-hidden>
          <Icon size={theme.icon.size.lg} stroke={theme.icon.stroke.sm} />
        </StyledIcon>
      )}
      <StyledTitle>{title}</StyledTitle>
      {isNonEmptyString(description) && (
        <StyledDescription>{description}</StyledDescription>
      )}
      {isDefined(action) && <StyledAction>{action}</StyledAction>}
    </StyledContainer>
  );
};
